import React, { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEO from "@/components/SEO";
import { ClariasLogo } from "@/components/ClariasLogo";
import { IndianHeritageBackdrop } from "@/components/IndianHeritageBackdrop";
import { ReportPreviewSection, JourneySection, TestimonialsSection, FaqSection, WhyFailSection, WhySolutionsSection, IntelligenceLayerSection, HeroPanelStrip } from "@/components/IntelligenceSections";

import { 
  Target, 
  Shield,
  Brain, 
  CheckCircle, 
  ArrowRight,
  AlertTriangle,
  BookOpen,
  TrendingUp,
  Clock,
  FileText,
  BarChart3,
  Zap,
  GraduationCap,
  Newspaper,
  PenTool,
  XCircle,
  CheckCircle2,
  Sparkles,
  Users,
  Star,
  AlertCircle,
  RefreshCw
} from "lucide-react";

// Scroll-triggered reveal (fade + rise). Respects prefers-reduced-motion.
const Reveal = ({ children, delay = 0, y = 28, className }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
};

const PublicLandingPage = () => {
  const navigate = useNavigate();

  // Smooth-scroll to a section when URL has a hash (e.g. /#pricing from another page)
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      // Defer to next tick so the DOM is fully rendered
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#0B1020]" data-testid="public-landing-page" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes floatY { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
        .font-editorial { font-family: 'Playfair Display', Georgia, serif; }
      `}</style>
      <SEO
        title="CLARIAS | UPSC & State PSC (Group 1 & 2) — Mains Evaluation, Daily CA & Prelims Practice"
        description="Examiner-grade Mains & Essay rubric evaluation, daily editorial-tagged Current Affairs, 154-chapter Prelims master material, MCQ generator & Strategy Analytics. Founding plans from ₹699/month."
        canonical="/"
      />
      
      {/* ========================================
          HERO — Left-Aligned Editorial + 6-Icon Stats Strip
          Brings back the IAS-premium architecture with refined Intelligence-Layer matter.
          ======================================== */}
      <section className="relative overflow-hidden">
        {/* Deep navy → midnight blue gradient + warm amber halo behind the right column */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1220] via-[#0F172A] to-[#0B1220]" />
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none opacity-30 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.30) 0%, rgba(245,158,11,0) 70%)' }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] pointer-events-none" />

        {/* V9 Hybrid — Indian heritage backdrop (Parliament + Ashoka Chakra + India Gate silhouette) */}
        <IndianHeritageBackdrop />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 md:pt-28 pb-20 md:pb-24">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-14 items-center">
            <div className="max-w-xl min-w-0">

            {/* ── Brand positioning pill (above H1) ───────────────────── */}
            <div
              className="inline-flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full px-5 py-2 backdrop-blur-sm animate-[fadeIn_0.6s_ease-out]"
              data-testid="hero-trust-pill"
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-white text-sm font-semibold">Decision Intelligence for UPSC & State PSC</span>
            </div>

            {/* ── H1 — Editorial, stacked with vertical & ──────────────── */}
            <div className="space-y-1 mt-10 animate-[fadeInUp_0.7s_ease-out_0.1s_both]">
              <h1 className="font-editorial text-[44px] sm:text-5xl md:text-6xl lg:text-[68px] xl:text-[72px] font-black text-white leading-[1.04] tracking-tight" data-testid="hero-headline-line1">
                Crack UPSC
              </h1>
              <h1 className="font-editorial text-4xl sm:text-4xl md:text-5xl font-black leading-[1.04] tracking-tight" aria-hidden="true">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">&amp;</span>
              </h1>
              <h1 className="font-editorial text-[44px] sm:text-5xl md:text-6xl lg:text-[68px] xl:text-[72px] font-black text-white leading-[1.04] tracking-tight" data-testid="hero-headline-line1b">
                State PSC
              </h1>
              <h1 className="font-editorial text-[44px] sm:text-5xl md:text-6xl lg:text-[68px] xl:text-[72px] font-black text-white leading-[1.04] tracking-tight pt-1" data-testid="hero-headline-line2">
                with <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">the Intelligence Layer.</span>
              </h1>
            </div>

            {/* ── Italic Editorial Sub-Thesis ──────────────────────────── */}
            <p
              className="font-editorial italic text-slate-300 text-xl md:text-2xl mt-9 animate-[fadeInUp_0.7s_ease-out_0.25s_both]"
              data-testid="hero-subthesis"
            >
              Strategy decides. Selection follows.
            </p>

            {/* ── Supporting copy — 3 short lines, positioning only ────── */}
            <p
              className="text-slate-300 text-base md:text-lg lg:text-xl max-w-xl mt-7 leading-relaxed animate-[fadeInUp_0.7s_ease-out_0.35s_both]"
              data-testid="hero-description"
            >
              Most aspirants know what to study.<br />
              <span className="text-white font-semibold">Few know why they lose marks.</span><br />
              CLARIAS helps you make better preparation decisions —<br />
              <span className="text-white font-semibold">for UPSC and State PSC (Group 1 &amp; Group 2).</span>
            </p>

            {/* ── Exam coverage strip ──────────────────────────────────── */}
            <div
              className="mt-7 flex items-center gap-2 flex-wrap animate-[fadeInUp_0.7s_ease-out_0.4s_both]"
              data-testid="hero-exam-coverage"
            >
              <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Built for</span>
              {["UPSC", "State PSC · Group 1", "State PSC · Group 2"].map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] text-slate-200 hover:border-amber-500/40 hover:text-white transition-colors"
                  data-testid={`exam-chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500" />
                  {label}
                </span>
              ))}
            </div>

            {/* ── CTA — primary diagnostic + secondary demo ────────────── */}
            <div className="mt-9 flex items-center gap-3 flex-wrap animate-[fadeInUp_0.7s_ease-out_0.45s_both]">
              <Button
                onClick={() => navigate('/auth')}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 rounded-xl font-semibold text-base md:text-lg shadow-lg shadow-amber-500/20 group transition-all"
                data-testid="get-started-btn"
              >
                Start Free
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* ── Micro-trust strip (left-aligned) ────────────────────── */}
            <div className="mt-6 flex items-center gap-5 text-sm md:text-base text-slate-300 flex-wrap animate-[fadeInUp_0.7s_ease-out_0.55s_both]">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> No credit card
              </span>
              <span className="text-slate-700">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-amber-400" /> Free forever tier
              </span>
              <span className="text-slate-700">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Target className="w-4 h-4 text-amber-400" /> 2-min strategy check
              </span>
            </div>

            </div>

            {/* Right column — scrollable live product dashboards (single product showcase) */}
            <div id="see-in-action" className="relative min-w-0 w-full scroll-mt-24" data-testid="hero-product-visual">
              <HeroPanelStrip />
            </div>

          </div>

        </div>
      </section>

      {/* Narrative arc: Problem → Ecosystem → Moat → Gap → Journey */}
      <WhyFailSection />

      {/* ========================================
          COMPLETE UPSC ECOSYSTEM — Product Hub Showcase (Light Section)
          ======================================== */}
      <section className="relative bg-[#F5F6F8] py-24 md:py-32" data-testid="one-platform-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-14 md:mb-16">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#2563EB] font-bold mb-3">One Platform. Every Stage.</p>
            <h2 className="font-editorial text-3xl md:text-5xl font-black text-[#0B1020] tracking-tight">
              The Complete <span className="text-[#2563EB]">UPSC Ecosystem.</span>
            </h2>
            <p className="text-slate-600 text-base md:text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
              Six focused hubs cover the full journey — Foundation to Interview. One login. Zero scattered PDFs.
            </p>
          </Reveal>

          {(() => {
            const hubs = [
              {
                icon: GraduationCap, title: 'Foundation Hub', tag: 'NCERT · Class 6–12',
                body: 'Build unshakeable basics with chapter-wise concept maps.',
                bullets: ['324+ Chapters', 'Concept Maps', 'PYQ Tagging'],
                iconBg: 'bg-blue-500/10', iconBorder: 'border-blue-500/30', iconColor: 'text-blue-600', accent: 'hover:border-blue-500/40',
              },
              {
                icon: Newspaper, title: 'Current Affairs Hub', tag: 'Daily · Editorials · PIB',
                body: 'Only the events with real exam yield — tagged to GS papers.',
                bullets: ['Daily Brief', 'Editorial Analysis', 'CA → MCQ'],
                iconBg: 'bg-emerald-500/10', iconBorder: 'border-emerald-500/30', iconColor: 'text-emerald-600', accent: 'hover:border-emerald-500/40',
              },
              {
                icon: Brain, title: 'Decision Intelligence', tag: 'The moat · Strategy · PYQ',
                body: 'The private intelligence layer on top of everything — it finds where you lose marks, adapts your strategy, and reveals what UPSC repeatedly rewards.',
                bullets: ['Topper Answer Intelligence', 'Decision Dashboard', 'Weakness Detection', 'PYQ Pattern Intelligence', 'Revision Intelligence', 'Performance Tracking'],
                iconBg: 'bg-blue-500/10', iconBorder: 'border-blue-500/30', iconColor: 'text-blue-600', accent: 'hover:border-blue-500/40', featured: true, wide: true,
              },
              {
                icon: Target, title: 'Prelims Hub', tag: 'GS · CSAT · PYQ',
                body: 'Chapter-wise master material and CSAT practice, with pattern intelligence that shows what actually repeats.',
                bullets: ['Chapter-wise Master Material', 'MCQ Practice Bank', 'CSAT Practice Suite', 'PYQ Analyser & Heatmap', 'Current Affairs → MCQ', 'Smart Revision Planner'],
                iconBg: 'bg-orange-500/10', iconBorder: 'border-orange-500/30', iconColor: 'text-orange-600', accent: 'hover:border-orange-500/40', featured: true,
              },
              {
                icon: PenTool, title: 'Mains Hub', tag: 'GS1–GS4 · Essay',
                body: 'Selection-level answer approaches and essay frameworks built around topper-style writing and UPSC trends.',
                bullets: ['Topper Answer Writing', 'Essay Frameworks', 'Trend-Based Questions', 'Model Structures'],
                iconBg: 'bg-amber-500/10', iconBorder: 'border-amber-500/30', iconColor: 'text-amber-600', accent: 'hover:border-amber-500/40', featured: true,
              },
              {
                icon: BookOpen, title: 'Optionals Hub', tag: 'Paper I & II',
                body: 'Dedicated engines for high-demand optionals, same intelligence layer.',
                bullets: ['Sociology', 'PSIR', 'Anthropology'],
                iconBg: 'bg-purple-500/10', iconBorder: 'border-purple-500/30', iconColor: 'text-purple-600', accent: 'hover:border-purple-500/40',
              },
            ];
            const Card = (h, gi, compact) => {
              const Ic = h.icon;
              return (
                <div
                  key={gi}
                  onClick={() => navigate('/auth')}
                  role="button"
                  tabIndex={0}
                  data-testid={`hub-card-${gi}`}
                  className={`group relative border rounded-2xl hover:-translate-y-1 transition-all duration-300 ${compact ? 'p-6' : 'p-8'} ${h.wide ? 'md:col-span-2 bg-gradient-to-br from-white to-blue-50/50' : 'bg-white'} ${h.featured ? 'border-[#2563EB]/25 ring-1 ring-[#2563EB]/15 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_55px_-22px_rgba(37,99,235,0.32)]' : 'border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_30px_-18px_rgba(0,0,0,0.18)]'} ${h.accent}`}
                >
                  {h.featured && (
                    <span className={`absolute -top-2.5 left-6 text-[9px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full text-white shadow-sm ${h.wide ? 'bg-[#2563EB]' : 'bg-gradient-to-r from-orange-500 to-amber-500'}`}>
                      {h.wide ? 'The Moat' : 'Flagship'}
                    </span>
                  )}
                  <div className={`rounded-xl ${h.iconBg} border ${h.iconBorder} flex items-center justify-center group-hover:scale-110 transition-transform ${compact ? 'w-10 h-10 mb-3' : 'w-14 h-14 mb-4'}`}>
                    <Ic className={`${compact ? 'w-5 h-5' : 'w-7 h-7'} ${h.iconColor}`} />
                  </div>
                  <p className={`uppercase tracking-[0.16em] font-bold mb-1.5 ${h.iconColor} ${compact ? 'text-[9px]' : 'text-[10px]'}`}>{h.tag}</p>
                  <h3 className={`font-editorial text-[#0B1020] font-black leading-tight mb-2 ${compact ? 'text-lg' : 'text-2xl md:text-3xl'}`}>{h.title}</h3>
                  <p className={`text-slate-600 leading-relaxed mb-4 ${compact ? 'text-[13px]' : 'text-sm md:text-base'} ${h.wide ? 'max-w-2xl' : ''}`}>{h.body}</p>
                  <ul className={`mb-5 ${h.wide ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5' : 'space-y-1.5'}`}>
                    {h.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-center gap-2 text-[13px] text-slate-700">
                        <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 ${h.iconColor}`} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-black/[0.06] flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500 group-hover:text-slate-700 transition-colors">Explore</span>
                    <div className={`w-7 h-7 rounded-full ${h.iconBg} border ${h.iconBorder} flex items-center justify-center group-hover:translate-x-1 transition-transform`}>
                      <ArrowRight className={`w-3.5 h-3.5 ${h.iconColor}`} />
                    </div>
                  </div>
                </div>
              );
            };
            const withIdx = hubs.map((h, gi) => ({ h, gi }));
            return (
              <>
                <Reveal>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#2563EB]">Flagship</span>
                    <span className="h-px flex-1 bg-black/[0.07]" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    {withIdx.filter((x) => x.h.featured).map(({ h, gi }) => Card(h, gi, false))}
                  </div>
                </Reveal>

                <Reveal delay={0.05}>
                  <div className="flex items-center gap-4 mt-14 mb-6">
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Also included</span>
                    <span className="h-px flex-1 bg-black/[0.07]" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                    {withIdx.filter((x) => !x.h.featured).map(({ h, gi }) => Card(h, gi, true))}
                  </div>
                </Reveal>
              </>
            );
          })()}

          {/* Bottom CTA strip */}
          <Reveal className="mt-14 text-center">
            <p className="text-slate-600 text-sm md:text-base mb-4">
              <span className="font-semibold text-[#0B1020]">6 hubs.</span> One login. Zero scattered PDFs.
            </p>
            <Button
              onClick={() => navigate('/auth')}
              size="lg"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-9 py-6 rounded-xl font-semibold text-base shadow-lg shadow-blue-600/20 group"
              data-testid="one-platform-cta-btn"
            >
              Get Full Access — Free Forever Tier
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* Moat → Gap → Journey (moved to follow the Ecosystem showcase) */}
      <IntelligenceLayerSection />
      <WhySolutionsSection />

      {/* Preparation Journey */}
      <JourneySection />

      {/* SECTION 9 · Trust */}
      <TestimonialsSection />

      {/* Free diagnostic — conversion nudge right before pricing */}
      <ReportPreviewSection />

      {/* ========================================
          SECTION 10 · PRICING
          ======================================== */}
      <section id="pricing" className="pt-24 md:pt-28 pb-24 md:pb-28 bg-white relative">
        <div className="max-w-5xl mx-auto px-6 space-y-14">
          <Reveal className="text-center space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563EB]">Pricing</p>
            <h2 className="font-editorial text-3xl md:text-5xl font-black text-[#0B1020] tracking-tight">
              Coaching costs ₹50,000+
            </h2>
            <p className="text-lg md:text-xl text-slate-600">
              CLARIAS costs less than one mistake.
            </p>
          </Reveal>

          <Reveal className="grid md:grid-cols-3 gap-6" delay={0.05}>
            {/* Free */}
            <Card className="bg-white border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_14px_36px_-20px_rgba(0,0,0,0.18)] rounded-2xl">
              <CardContent className="p-7 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-[#0B1020]">FREE</h3>
                  <p className="text-slate-500 text-sm mt-1">Explore decision intelligence</p>
                </div>
                <div className="text-3xl font-bold text-[#0B1020]">₹0</div>
                <ul className="space-y-3">
                  {[
                    "Sample PYQ analysis",
                    "Limited practice",
                    "Basic current affairs"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-600 text-sm">
                      <CheckCircle className="w-4 h-4 text-slate-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/auth')} variant="outline" className="w-full rounded-full border-slate-300 text-slate-700 hover:bg-slate-50">
                  Start Free
                </Button>
              </CardContent>
            </Card>

            {/* PRO */}
            <Card className="bg-white border-[#2563EB]/30 ring-1 ring-[#2563EB]/20 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_50px_-24px_rgba(37,99,235,0.35)] rounded-2xl relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2563EB] text-white text-xs font-bold px-4 py-1 rounded-full">
                RECOMMENDED
              </div>
              <CardContent className="p-7 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-[#0B1020]">CLARIAS PRO</h3>
                  <p className="text-slate-500 text-sm mt-1">Complete Prelims + Mains system</p>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-slate-400 line-through">₹23,999</span>
                  <span className="text-3xl font-bold text-[#0B1020]">₹6,999</span>
                </div>
                <p className="text-xs text-emerald-600 font-medium -mt-2">Founding pricing • Save ₹17,000</p>
                <button
                  type="button"
                  onClick={() => navigate('/auth?upgrade=true')}
                  className="-mt-2 text-left text-xs text-slate-500 hover:text-[#2563EB] transition-colors group block w-full"
                  data-testid="see-other-plans-link"
                >
                  or start at <span className="text-[#0B1020] font-semibold">₹699/month</span>
                  <span className="text-[#2563EB] ml-1 inline-block group-hover:translate-x-0.5 transition-transform">→</span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">Monthly &amp; Quarterly plans available</span>
                </button>
                <ul className="space-y-3">
                  {[
                    "Full Prelims Intelligence",
                    "Complete CSAT Suite",
                    "GS I–IV + Essay",
                    "Smart Answer Evaluation",
                    "Valid till Mains 2027 (2 attempts)"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-700 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#2563EB]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/auth?upgrade=true')} className="w-full rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                  Unlock PRO
                </Button>
              </CardContent>
            </Card>

            {/* Optionals */}
            <Card className="bg-white border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_14px_36px_-20px_rgba(0,0,0,0.18)] rounded-2xl">
              <CardContent className="p-7 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-[#0B1020]">Optionals</h3>
                  <p className="text-slate-500 text-sm mt-1">Dedicated Paper I & II engines</p>
                </div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-slate-400 line-through text-lg">₹5,999</span>
                  <span className="text-3xl font-bold text-[#0B1020]">₹1,999</span>
                  <span className="text-lg text-slate-500 font-normal">each</span>
                </div>
                <ul className="space-y-3">
                  {[
                    "Sociology",
                    "Anthropology",
                    "Public Administration",
                    "PSIR"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-600 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#2563EB]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/auth')} variant="outline" className="w-full rounded-full border-slate-300 text-slate-700 hover:bg-slate-50">
                  View Optionals
                </Button>
              </CardContent>
            </Card>
          </Reveal>

          <Reveal className="text-center" delay={0.05}>
            <p className="text-slate-500 italic">
              No guarantees. No false promises.<br />
              <span className="text-slate-600">Just reduced failure probability.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ========================================
          SECTION 11 · FAQ
          ======================================== */}
      <FaqSection />

      {/* ========================================
          SECTION 12 · FINAL CTA
          ======================================== */}
      <section className="py-28 bg-[#0B1020] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-blue-600/10 to-transparent" />
        
        <Reveal className="relative max-w-4xl mx-auto px-6 text-center space-y-8">
          <div className="space-y-4">
            <h2 className="font-editorial text-4xl md:text-6xl font-black text-white leading-tight tracking-tight">
              Stop Guessing.
            </h2>
            <h2 className="font-editorial text-4xl md:text-6xl font-black leading-tight tracking-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">Start Deciding.</span>
            </h2>
          </div>

          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Every preparation decision has consequences. CLARIAS helps identify them before the exam does.
          </p>

          <div className="pt-6">
            <Button 
              onClick={() => navigate('/free-diagnostic')}
              size="lg" 
              className="rounded-full px-12 py-8 text-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white shadow-lg shadow-blue-600/30"
              data-testid="final-cta-btn"
            >
              Start Free Diagnostic
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </Reveal>
      </section>

      {/* ========================================
          FOOTER
          ======================================== */}
      <footer className="py-12 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img
                src="/clarias-torch-logo-transparent.png"
                alt="Clarias Logo"
                className="h-9 object-contain"
              />
              <span className="text-lg font-semibold text-white">CLARIAS</span>
              <span className="text-slate-600 hidden sm:inline">|</span>
              <span className="text-slate-500 text-sm hidden sm:inline">Clarity over Chaos</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/about" className="hover:text-slate-300">About</Link>
              <Link to="/privacy-policy" className="hover:text-slate-400">Privacy</Link>
              <Link to="/terms-of-service" className="hover:text-slate-400">Terms</Link>
              <Link to="/refund-policy" className="hover:text-slate-400">Refunds</Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center">
            <p className="text-sm text-slate-600">
              © 2025 CLARIAS. Built for aspirants who value decisions over noise.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLandingPage;
